import type { ShipmentAddress, ShipmentProduct } from "@/types/shipments"

import type {
  MissionCoupDeCoeur,
  Submission,
  SubmissionCounts,
} from "./submissions"

/**
 * OKS-29 — Inertia prop shapes for the brand-side Mission CRUD surface
 * (Index / New / Edit / Show).
 *
 * Locked at TRD §"Inertia Props" publish + bilateral with LQE's contract
 * spec (`spec/requests/contract_specs/missions_props_spec.rb`). Every
 * snake_case field mirrors the Rails Inertia serializer 1:1 (see
 * `MissionsController#serialise_attributes`); the FE never re-shapes on
 * the wire.
 */

// ─── Type / status enums ───────────────────────────────────────────────

/** Mission type enum (PRD §Scope, frozen V1). */
export type MissionType = "drop" | "promotional" | "open"

export const MISSION_TYPES: readonly MissionType[] = [
  "drop",
  "promotional",
  "open",
] as const

/** Mission status enum — V1 ships {draft, active}; completed/archived = V2. */
export type MissionStatus = "draft" | "active"

export const MISSION_STATUSES: readonly MissionStatus[] = [
  "draft",
  "active",
] as const

/**
 * OKS-467 — Mission audience enum. `specific` (default) targets only
 * explicitly-assigned members; `all_crew` is a durable rule that
 * auto-assigns future joiners. Drives the brand "All crew" badge.
 */
export type MissionAudience = "specific" | "all_crew"

/** V1 content-format catalog (PRD §3 lock). */
export type MissionContentFormat =
  | "unboxing"
  | "tutorial"
  | "review"
  | "grwm"
  | "haul"
  | "other"

export const MISSION_CONTENT_FORMATS: readonly MissionContentFormat[] = [
  "unboxing",
  "tutorial",
  "review",
  "grwm",
  "haul",
  "other",
] as const

// ─── ProseMirror brief shape ───────────────────────────────────────────

/**
 * Minimal ProseMirror node shape for the TipTap brief. Loosely typed by
 * design — server-side `Missions::BriefSchemaValidator` is the source of
 * truth for the allow-list (nodes: doc, paragraph, bulletList,
 * orderedList, listItem, hardBreak, text; marks: bold, italic, link).
 * The FE editor enforces the same as UX, but the server is the floor.
 */
export interface ProseMirrorMark {
  type: string
  attrs?: Record<string, unknown>
}

export interface ProseMirrorNode {
  type: string
  attrs?: Record<string, unknown>
  marks?: ProseMirrorMark[]
  content?: ProseMirrorNode[]
  text?: string
}

export interface ProseMirrorDoc extends ProseMirrorNode {
  type: "doc"
}

// ─── Attachments ───────────────────────────────────────────────────────

/**
 * Attachment metadata as serialized by `MissionsController#serialise_
 * attachments`. Carries metadata + the brand-scoped proxy URL — never
 * the blob bytes (TRD-OKS-29 §8 risk mitigation).
 *
 * OKS-186 adds `id` and `url`:
 *   - `id` is the `ActiveStorage::Attachment.id` row id (the per-mission
 *     join row id), used by the proxy route as the path segment after
 *     `/brand/missions/:mission_id/attachments/`.
 *   - `url` is the proxy URL — the FE renders this as the anchor `href`
 *     in the Mission Show Attachments card. NEVER construct the URL
 *     client-side; always read this prop verbatim. The URL points at
 *     the `MissionAttachmentsController#show` action which brand-gates
 *     every byte and returns 404 on any cross-brand probe.
 *
 * `signed_id` is retained for the New/Edit direct-upload echo (the form
 * sends `mission[files][]` as signed_id strings; this prop carries the
 * echoed value back for the Edit page so attachments persist across
 * re-renders without re-uploading).
 */
export interface MissionAttachment {
  /** ActiveStorage::Attachment row id — path segment for the proxy URL. */
  id: number
  signed_id: string
  filename: string
  byte_size: number
  content_type: string
  /**
   * Brand-scoped proxy URL — `/brand/missions/:mission_id/attachments/:id`.
   * Render verbatim as the anchor `href`. NEVER use `rails_blob_path` /
   * `rails_blob_url` / `rails_storage_proxy_path` for mission attachments.
   */
  url: string
}

// ─── Mission shapes ────────────────────────────────────────────────────

/**
 * Row shape for the Index page table — minimal columns for the list view.
 * Detail keys live on `MissionAttributes` + `MissionShowProps`.
 *
 * OKS-31 — adds per-row `collab_count` (sourced from
 * `Missions::CollabCountsBatch` server-side; never N+1) and per-row
 * policy flags `can_edit` / `can_assign` so the kebab quick-action
 * menu renders without re-deriving role × state on the client.
 */
export interface MissionRow {
  id: number
  title: string
  mission_type: MissionType
  status: MissionStatus
  /** OKS-467 — `all_crew` renders the "All crew" badge. */
  audience: MissionAudience
  /** ISO 8601 date string or null. */
  deadline: string | null
  /** ISO 8601 timestamp. */
  created_at: string
  /**
   * OKS-31 — `{completed}/{total}` per-row metric. `total === 0`
   * indicates "no collabs assigned yet" — the FE badge renders
   * `—/—` (NOT `0/0`) per PRD Open-Q4.
   */
  collab_count: { completed: number; total: number }
  /**
   * OKS-641 — per-mission count of `pending` submissions awaiting brand
   * review (approved/flagged excluded). Aggregate integer only, sourced
   * from `Missions::PendingReviewCountsBatch`. `0` when nothing is
   * pending — the FE badge renders nothing at 0.
   */
  pending_review_count: number
  can_edit: boolean
  can_assign: boolean
  /**
   * OKS-609 — resolved `:card` cover rendition for the list thumbnail:
   * attached variant URL → legacy URL → `null` (→ branded fallback).
   */
  cover_image_display_url: string | null
}

// ─── OKS-31 — list filter / sort / pagination ─────────────────────────

export type MissionListStatus = "draft" | "active" | "all"
export const MISSION_LIST_STATUSES: readonly MissionListStatus[] = [
  "draft",
  "active",
  "all",
] as const

export type MissionListMissionType = MissionType | "all"
export const MISSION_LIST_TYPES: readonly MissionListMissionType[] = [
  "drop",
  "promotional",
  "open",
  "all",
] as const

export type MissionListDeadlineWindow = "overdue" | "next_7" | "next_30" | "all"
export const MISSION_LIST_DEADLINE_WINDOWS: readonly MissionListDeadlineWindow[] =
  ["overdue", "next_7", "next_30", "all"] as const

export type MissionListSortField = "deadline" | "created_at" | "title"
export const MISSION_LIST_SORT_FIELDS: readonly MissionListSortField[] = [
  "deadline",
  "created_at",
  "title",
] as const

export type MissionListSortDir = "asc" | "desc"

/**
 * Echo of the `Missions::ListQuery#applied` Hash — server reflects the
 * resolved (post-whitelist) filter set so the FE chips never show a
 * value the server didn't actually apply.
 */
export interface MissionListFilters {
  status: MissionListStatus
  mission_type: MissionListMissionType
  deadline_window: MissionListDeadlineWindow
  q: string | null
}

export interface MissionListSort {
  field: MissionListSortField
  dir: MissionListSortDir
}

export interface MissionListFilterOptions {
  statuses: readonly MissionListStatus[]
  mission_types: readonly MissionListMissionType[]
  deadline_windows: readonly MissionListDeadlineWindow[]
}

/** 5-key pagy projection. */
export interface MissionListPagination {
  page: number
  pages: number
  count: number
  prev: number | null
  next: number | null
}

/**
 * Full attribute shape for New / Edit / Show pages. New defaults are blank
 * (controller emits `Mission.new(status: "draft")` then serializes); Edit
 * emits the persisted values + signed_ids of attached files.
 */
export interface MissionAttributes {
  id: number | null
  mission_type: MissionType | null
  status: MissionStatus
  /** OKS-467 — `all_crew` renders the "All crew" badge on the Show header. */
  audience: MissionAudience
  title: string
  brief: ProseMirrorDoc | Record<string, unknown> | null
  product_info: string | null
  hashtags: string | null // semicolon-joined wire shape
  mentions: string | null
  content_format: MissionContentFormat | null
  content_format_other: string | null
  deadline: string | null // ISO date
  /**
   * OKS-456 — per-mission reward overrides. `null` = inherit the brand
   * default (resolved at award time); a number (incl. `0`) is an
   * explicit per-mission value. Replaces the deprecated `xp_reward`,
   * which is severed from payout and no longer serialised.
   */
  xp_base_override: number | null
  xp_on_time_bonus_override: number | null
  xp_engagement_bonus_override: number | null
  coins_base_override: number | null
  coins_on_time_bonus_override: number | null
  promo_code: string | null
  ecommerce_url: string | null
  attachments: MissionAttachment[]
}

/**
 * OKS-456 — resolved brand reward defaults the mission-form override
 * fields inherit from. Keys mirror the override column names so each
 * placeholder/helper pairs directly to its field. Emitted by
 * `MissionsController#brand_reward_defaults`.
 */
export interface MissionRewardDefaults {
  xp_base_override: number
  xp_on_time_bonus_override: number
  xp_engagement_bonus_override: number
  coins_base_override: number
  coins_on_time_bonus_override: number
}

/**
 * OKS-447 — Media sub-shape the controller merges into the form `mission`
 * prop (New + Edit only; the Show page emits plain `MissionAttributes`).
 * Resolved server-side via `Mission#cover_image_display_url` +
 * `#cover_image_attached?` / `#cover_image_legacy_only?` so the FE never
 * re-derives the cover precedence.
 */
export interface MissionFormMedia {
  /** Resolved cover URL: attached `:card` variant → legacy URL → null. */
  cover_image_display_url: string | null
  /** True when an Active Storage cover blob exists (gates Remove). */
  has_cover_image: boolean
  /** True when the cover is a legacy pasted-URL only (no blob) — drives
   *  the "imported from a link" note. */
  cover_image_is_legacy: boolean
  /** Attached brief-video filename, or null. Re-edit echo for the chip. */
  brief_video_filename: string | null
  /** Attached brief-video signed_id, or null. Lets Edit keep the blob
   *  without re-upload. */
  brief_video_signed_id: string | null
}

/**
 * OKS-495 — Gift-product sub-shape the controller merges into the form
 * `mission` prop (New + Edit only), `null` when the gifting mission has no
 * product yet. `value` is the major-unit decimal string (e.g. "19.99") from
 * the `MissionProduct#value` virtual, so the amount input round-trips
 * without the operator typing cents.
 */
export interface MissionProductFormData {
  id: number
  name: string
  quantity: number
  value: string | null
  currency: string | null
  sku: string | null
  stock: number | null
  /**
   * OKS-935 — per-mission gift TTL override in days (AC2). `null` = inherit
   * the variant's, then the product's; when none carries a value possession
   * never expires and a repeat gift always warns.
   *
   * ⚠️ Belongs to the gift THIS mission ships and is read by a LATER mission's
   * recency guard — never consulted for this mission's own preview (PRD D6).
   */
  gift_ttl_days: number | null
  /** Resolved photo URL (signed blob), or null when no photo attached. */
  photo_url: string | null
  /** True when an Active Storage photo blob exists (gates Remove). */
  has_photo: boolean

  // ---- OKS-933 ----
  /**
   * Catalog product this gift was attached from, or `null` for a legacy
   * free-text row, a detached one, or one whose product was deleted.
   */
  product_id: number | null
  /** The brand set a variant restriction. Survives the members vanishing. */
  variant_pinned: boolean
  /** Live member rows. Empty + `variant_pinned` = the orphaned pin. */
  pinned_variant_ids: number[]
  /** Server-computed `variant_pinned && members.empty?`. */
  pin_orphaned: boolean
  /** Display-only catalog data for the S2 summary card. */
  product: AttachedCatalogProduct | null

  // ---- OKS-1000 ----
  /**
   * What a creator would see if this row carried NO value of its own — the
   * server's ladder below rung 1 (`MissionProduct#fallback_value`). Drives the
   * value field's hint, which promises what happens when the brand leaves the
   * field blank. `null` cents ⇒ no fallback exists ⇒ the hint promises nothing.
   *
   * ⚠️ NOT the effective value, and not `product.min_price_cents`. The
   * catalogue minimum is blind to the variant pin and to the range flag — the
   * hint's first version read it directly and was correct on one catalogue
   * mission in four. The effective value is wrong in a subtler way: it includes
   * the row's own saved amount, which is the number an operator clearing the
   * field is in the act of deleting.
   *
   * ⚠️ Do not re-derive any of this in the frontend. One resolver, one place
   * (BRIEF D1); a second copy here is the one that would drift, because it is
   * the one no rung-boundary spec covers.
   */
  fallback_value_cents: number | null
  /** ISO-4217 alpha-3; `null` pairs with a null `fallback_value_cents`. */
  fallback_currency: string | null
  /** The fallback is a FLOOR (a spanning pin, or a catalogue range). */
  fallback_is_range: boolean
}

/**
 * OKS-933 — the catalog product behind an attached gift.
 *
 * ⚠️ DISPLAY ONLY. What the mission GIFTS is the snapshot on
 * `MissionProductFormData` (`name` / `value` / `currency` / `sku`); this is
 * never the source for those. Rendering `product.title` where the snapshot
 * belongs re-derives at render a fact fixed at attach — the defect
 * `mission_products.currency` exists to prevent (AC6).
 */
export interface AttachedCatalogProduct {
  id: number
  title: string
  image_url: string | null
  archived: boolean
  manual: boolean
  min_price_cents: number | null
  max_price_cents: number | null
  currency: string
  /**
   * ⚠️ Present so the pin renders on a page LOAD, not only inside a picker
   * session. Its absence made UX §5 row 4 unrenderable and broke AC13 —
   * the collapsible expanded to zero checkboxes on every reload of an
   * attached mission. Includes ARCHIVED variants; see `CatalogPickerVariant`.
   */
  variants: CatalogPickerVariant[]
}

/**
 * OKS-933 — the states of UX Spec §5, keyed on the TUPLE
 * (`product_id`, `variant_pinned`, live members) and never on where a block
 * sits on the page. UX §5 became a state table precisely because placement
 * ("below the summary in S2") silently absorbed a new arm created later.
 *
 * ⚠️ `pin-orphaned` and `detached-orphan` differ ONLY by `product_id`. A branch
 * testing "pinned with no members" without checking `product_id` renders S2's
 * summary card over a null product — no title, price or thumbnail source.
 *
 * ⚠️ `pinned` is the INTACT pin even when some members are archived: the sweep
 * only archives, so those rows survive and render checked + disabled. Only
 * ZERO live members is an orphan.
 */
export type MissionProductPinState =
  | "unpinned"
  | "pin-orphaned"
  | "pinned"
  | "detached-orphan"

export function missionProductPinState(
  product: MissionProductFormData | null,
): MissionProductPinState {
  if (!product?.variant_pinned) return "unpinned"
  if (product.pinned_variant_ids.length > 0) return "pinned"
  return product.product_id == null ? "detached-orphan" : "pin-orphaned"
}

/** OKS-933 — one row in the catalog picker (`catalog_products.products`). */
export interface CatalogPickerProduct {
  id: number
  title: string
  image_url: string | null
  manual: boolean
  min_price_cents: number | null
  max_price_cents: number | null
  currency: string
  variant_count: number
  variants: CatalogPickerVariant[]
}

export interface CatalogPickerVariant {
  id: number
  title: string
  sku: string | null
  price_cents: number | null
  /**
   * ⚠️ Always `false` from the picker (archived variants are excluded there,
   * AC2) and meaningful only on an ATTACHED product, where archived members
   * must keep rendering: the sweep archives rather than deletes, so a pinned
   * variant survives and shows checked + disabled + the chip (UX §5 row 4).
   * One wire shape across both surfaces rather than two that drift.
   */
  archived: boolean
}

/**
 * OKS-933 — the deferred `catalog_products` prop.
 *
 * ⚠️ `truncated` is load-bearing, not telemetry. The server caps at
 * `PICKER_LIMIT`; at the stated 2000-SKU scale a silent cut reads to the brand
 * as "you don't have that product" and is indistinguishable on screen from a
 * genuine no-match.
 *
 * ⚠️ `has_any` / `has_active` exist because an empty `products` array is
 * identical across P1 (no products), P3 (query matched nothing) and P5 (all
 * archived). The count cannot discriminate them; these flags can.
 */
export interface CatalogPickerPayload {
  products: CatalogPickerProduct[]
  query: string
  truncated: boolean
  has_any: boolean
  has_active: boolean
  store: CatalogPickerStore | null
}

/** Mirrors `BrandSettingsProductsController#serialize_store`'s subset. */
export interface CatalogPickerStore {
  status: string
  never_synced: boolean
  sync_in_progress: boolean
  last_synced_at: string | null
}

/** Form-only gift-product wrapper merged into the `mission` prop. */
export interface MissionFormProduct {
  /** Authored gift product, or null when none exists yet. */
  /**
   * OKS-990 — the offered SET, in `(position, id)` order. `[]` rather than null
   * when nothing is offered, so the form has no third case to branch on.
   */
  mission_products: MissionProductFormData[]
}

/** Full form attribute shape (New / Edit) — base attributes + media + product. */
export type MissionFormAttributes = MissionAttributes &
  MissionFormMedia &
  MissionFormProduct

/** Form options the controller emits alongside the mission attributes. */
export interface MissionFormOptions {
  mission_types: readonly MissionType[]
  content_formats: readonly MissionContentFormat[]
}

// ─── Page props (Inertia) ──────────────────────────────────────────────

export interface MissionsIndexProps {
  missions: MissionRow[]
  is_empty: boolean
  /** OKS-31 — filter / sort / pagy contract additions. */
  filters: MissionListFilters
  sort: MissionListSort
  pagination: MissionListPagination
  filter_options: MissionListFilterOptions
}

export interface MissionsFormProps {
  mission: MissionFormAttributes
  options: MissionFormOptions
  /** OKS-456 — resolved brand reward defaults for override placeholders. */
  brand_defaults: MissionRewardDefaults
  /**
   * OKS-928 — `mission_type` is permanently frozen once the mission has at
   * least one collab (`before_update :type_frozen_when_collabs_present`).
   * Server-derived per render, never client state: a mission whose collabs
   * were all removed unlocks naturally. Always `false` on New.
   */
  type_locked: boolean
  /**
   * OKS-933 — deferred picker payload. `undefined` only while Inertia is
   * resolving the deferred prop; the KEY is always present on the page.
   */
  catalog_products?: CatalogPickerPayload
  /** OKS-933 (D2) — gates Detach only. Attach is never collabs-gated. */
  can_detach_product: boolean
}

export interface MissionShowProps {
  mission: MissionAttributes & {
    created_at: string
    created_by: { id: number; name: string } | null
    /**
     * OKS-609 — resolved `:card` cover rendition for the detail hero:
     * attached variant URL → legacy URL → `null` (→ branded fallback).
     */
    cover_image_display_url: string | null
  }
  can_edit: boolean
  can_destroy: boolean
  /**
   * OKS-485 — gates the explicit "Assign to all crew" audience toggle on
   * the Show header. True for brand/assigned-agency admins; false for OKS
   * Admin (read-only badge only) and any non-write viewer.
   */
  can_set_audience: boolean
  /**
   * OKS-34 — collabs panel data. Server-driven; FE renders the table and
   * row-level actions without re-deriving role × state.
   *
   * OKS-31 — extended with `state_filter` + `state_counts` + `pagination`
   * so the state-filter chip row above the panel can drive a server-side
   * `?state=<x>` partial reload (`only: ['collabs']`). Funnel-bar clicks
   * mutate the same URL param — chip and bar always agree because they
   * read the same source.
   */
  collabs: BrandMissionCollabsPanel
  /**
   * OKS-31 — Funnel data. 4 ORDERED stages (assigned / accepted /
   * posted / completed). Drop / Promotional missions render the same
   * 4 stages PLUS an FE-only `Alert` keyed off `mission.mission_type`
   * (no `banner_key` is present in BE props — locked with LQE D12/D13).
   */
  funnel: MissionFunnelStage[]
  /**
   * OKS-31 — Metrics row. 6 raw counts + 3 derived rates. Each rate is
   * `null` when its denominator is 0 (FE renders `—`).
   */
  metrics: MissionMetrics
  /**
   * OKS-35 — In-page Sheet payload. Populated by `MissionsController#show`
   * only when `?submissions=<collab_id>` is in the URL. Loaded via
   * partial reload (`only: ['submissions_for_open_collab']`) so opening
   * the Sheet doesn't refetch the rest of the page. `null` when the
   * Sheet is closed or the requested collab is not on this mission.
   */
  submissions_for_open_collab: {
    collab_id: number
    creator_handle: string | null
    /**
     * OKS-399 — gates the approve-dialog kudo composer (FR-11). Derived
     * once for the sheet from `KudoPolicy#create?` (identical role set to
     * `SubmissionPolicy#approve?`). When false, the dialog shows plain
     * approve; the auto-checks panel still renders.
     */
    can_send_kudo: boolean
    /**
     * OKS-964 — the MISSION's one Coup de cœur, or null when unpicked.
     * Mission-level while the Sheet is collab-level, so a Sheet on any collab
     * can render the cap line that explains §2.3's silent rows (PRD OQ-4).
     */
    coup_de_coeur: MissionCoupDeCoeur | null
    submissions: Submission[]
  } | null
}

// ─── OKS-31 — Funnel + metrics shapes ─────────────────────────────────

// OKS-212 — widened to the real 8-value backend union. The backend has
// emitted all 8 stages for Promotional since OKS-33
// (`FUNNEL_STAGES_PROMOTIONAL` in `missions_controller`); the type
// lagged at the original 4. Open/Drop funnels still supply the 4-stage
// subset. The synthetic mobile-fold "shipping" row is NOT a backend
// stage — it lives in a component-internal `MobileFunnelRow` type — so
// it is deliberately kept OUT of this union.
export type MissionFunnelStageKey =
  | "assigned"
  | "accepted"
  | "purchased"
  | "to_ship"
  | "shipped"
  | "delivered"
  | "posted"
  | "completed"

export interface MissionFunnelStage {
  stage: MissionFunnelStageKey
  count: number
  /** i18n key under `missions.dashboard.funnel.stages.*`. */
  label_key: string
}

export interface MissionMetrics {
  assigned: number
  accepted: number
  posted: number
  completed: number
  declined: number
  issue: number
  /** Each rate is in `[0.0, 1.0]` OR null when the denominator is 0. */
  accept_rate: number | null
  post_rate: number | null
  completion_rate: number | null
}

/**
 * Collab state filter alphabet on the dashboard chip-row + URL `?state=`.
 * `'all'` is the default + canonical "no filter" value.
 *
 * OKS-1010 — the alphabet is every `Collab::STATES` member plus `'all'`.
 * That identity is not enforced by this file: the array below is annotated
 * with this union rather than derived from it, so adding a member HERE
 * without adding it to the array is silently legal (the reverse is a type
 * error). `spec/i18n/oks1010_chip_alphabet_labels_spec.rb` is what holds
 * the two sides — and this file — to `Collab::STATES`.
 */
export type MissionDashboardStateFilter =
  | "all"
  | "assigned"
  | "applied"
  | "accepted"
  | "rejected"
  | "purchased"
  | "to_ship"
  | "shipped"
  | "delivered"
  | "posted"
  | "completed"
  | "declined"
  | "issue"
  | "expired"
  | "reopen_requested"

// OKS-31 → OKS-33 → OKS-1010 — the alphabet is `['all', *Collab::STATES]`,
// in that order: `'all'` first, then every Collab state in lifecycle order
// as its own author declared them. Ordering is therefore mechanical, not
// curated — a new state joins at the position `Collab::STATES` gives it, and
// no placement debate is re-opened. That order also happens to follow the
// operator's mental progression through the funnel (the pre-/post-success
// buckets are shared across Promotional / Drop / Open; the workflow-specific
// buckets fall in state order), which is why it was hand-curated to the same
// sequence before it was stated as a rule.
//
// Chips with `count: 0` still render so the operator sees the empty bucket
// exists; UX trade-off favours discoverability over chrome reduction at Paalm
// scale.
export const MISSION_DASHBOARD_STATE_FILTERS: readonly MissionDashboardStateFilter[] =
  [
    "all",
    "assigned",
    "applied",
    "accepted",
    "rejected",
    "purchased",
    "to_ship",
    "shipped",
    "delivered",
    "posted",
    "completed",
    "declined",
    "issue",
    // OKS-483's deadline-lapse pair, surfaced as chips by OKS-1010. They were
    // reaching the browser in `state_counts` the whole time; only the chip row
    // was short.
    "expired",
    "reopen_requested",
  ] as const

// ─── OKS-34 — Brand Mission Collabs panel (Mission show) ──────────────

/**
 * Single row in the brand-side Mission show "Collabs (N)" panel. Mirrors
 * `Collabs::BrandRowSerializer` with `surface: :mission_panel` (OKS-1038; the
 * crew member page's row is `CrewMemberCollabRow` in `types/crew.ts`).
 *
 * Per-row policy bag (`can_*`): backend computes the
 * (role × state × type) gate at the serializer; FE never re-derives.
 *
 * OKS-32 widens with 4 Drop-only flags
 * (`can_accept_application`, `can_reject_application`,
 * `can_mark_shipped`, `can_mark_delivered`) plus a `tracking_number`
 * field and 5 new audit timestamps (`applied_at`, `rejected_at`,
 * `to_ship_at`, `shipped_at`, `delivered_at`).
 */
/**
 * OKS-934 — one selectable variant in the ship dialog.
 *
 * ⚠️ `archived: true` options are STILL SELECTABLE — the deliberate opposite of
 * OKS-933's pin editor, where archived members render disabled. That surface
 * configures what a mission MAY gift (forward-looking); this one records what
 * WAS shipped (retrospective). Different tense, different rule (PRD §3).
 */
export interface ShipVariantOption {
  id: number
  title: string
  sku: string | null
  archived: boolean
}

/**
 * OKS-934 — the creator's sizes, advisory only.
 *
 * ⚠️ This key is ABSENT from the row outside the shipment window, never
 * present-and-null (PRD §4). Branch on PRESENCE (`in` / `!== undefined`), never
 * on truthiness of a value you assume is always sent — the whole exposure gate
 * is that the key is not added.
 */
export interface CreatorSizes {
  apparel: string | null
  shoe: string | null
}

export interface BrandMissionCollabRow {
  id: number
  /** One of Collab::STATES — 12-state alphabet post-OKS-33. */
  state:
    | "assigned"
    | "applied"
    | "accepted"
    | "rejected"
    | "purchased"
    | "to_ship"
    | "shipped"
    | "delivered"
    | "posted"
    | "completed"
    | "declined"
    | "issue"
    // OKS-483 — deadline-lapse lifecycle (brand panel surfaces both: expired
    // as a muted terminal hint, reopen_requested with Approve/Decline).
    | "expired"
    | "reopen_requested"
  /** ISO8601 — most recent of the per-state audit timestamps. */
  last_state_change_at: string | null
  /** Per-state audit timestamps. ISO8601 or null. */
  assigned_at: string | null
  applied_at: string | null
  accepted_at: string | null
  rejected_at: string | null
  /** OKS-33 — Promotional-only audit timestamp. Nil on Open / Drop. */
  purchased_at: string | null
  to_ship_at: string | null
  shipped_at: string | null
  delivered_at: string | null
  posted_at: string | null
  completed_at: string | null
  issue_at: string | null
  /** Brand note — populated iff state === "issue". */
  issue_note: string | null
  /**
   * OKS-32 — Drop tracking. Plain text, ≤80 chars, nullable. Visible
   * to the brand operator on `shipped` / `delivered` rows; rendered
   * inline in the last-activity cell. Edit via the small pencil icon
   * (re-opens MarkShippedDialog in `edit` mode).
   */
  tracking_number: string | null
  /**
   * OKS-495 — Optional shipment-display fields, set on mark-shipped.
   * `tracking_url` is an https deep-link (powers the creator's "Track"
   * button); `delivery_eta` an ISO date (shown to the creator as the ETA).
   * Both nullable; they prefill the mark-shipped dialog's edit mode.
   */
  tracking_url: string | null
  delivery_eta: string | null
  creator: {
    id: number
    name: string
    handle: string | null
    avatar_url: string | null
  }
  can_mark_complete: boolean
  can_flag_issue: boolean
  // OKS-32 — Drop brand events.
  can_accept_application: boolean
  can_reject_application: boolean
  can_mark_shipped: boolean
  can_mark_delivered: boolean
  // OKS-33 — Promotional brand events.
  can_mark_purchased: boolean
  can_mark_ready_to_ship: boolean
  // OKS-483 — reopen-request resolution (true only on a `reopen_requested`
  // row). The brand approves (→ submission-ready) or declines (→ expired).
  can_approve_reopen: boolean
  /** OKS-934 — snapshot label of the variant shipped; null when none. */
  shipped_variant_label?: string | null
  /**
   * OKS-1028 — the recorded variant's id; null when none recorded or the
   * variant was hard-deleted. Same emitter as the queue row
   * (`Shipments::ProductPayload#call`); read only by `correctionOffered`.
   */
  shipped_product_variant_id?: number | null
  /**
   * OKS-995 — what this collab is shipping: the product it CONSUMES, carrying
   * the set its ship dialog may offer (`shippable_variants`).
   *
   * ⚠️ PER COLLAB, not per mission. On a multi-product drop the panel used to
   * read one mission-level list built from the FIRST offered product, while
   * the write path floors on the collab's own — so the dialog offered ids the
   * server refused. Same `Shipments::ProductPayload` block the queue row
   * carries, so the two surfaces cannot describe one parcel two ways.
   *
   * ⚠️ Optional here for the reason `types/shipments.ts` records about its own
   * copy: frozen OKS-984 fixtures build complete row literals, so `tsc` cannot
   * catch a dropped merge and only a request-spec key-set pin can.
   */
  product?: ShipmentProduct | null
  /** ⚠️ OKS-934 — ABSENT outside the shipment window. Never null-when-closed. */
  creator_sizes?: CreatorSizes
  /**
   * OKS-984 — the gated creator address.
   *
   * ⚠️ ABSENT (not null) outside the reveal window — `to_ship` on a gifting
   * mission. Branch on PRESENCE, exactly like `creator_sizes` above. Same
   * payload the Shipments queue emits; see `types/shipments#ShipmentAddress`.
   */
  shipping_address?: ShipmentAddress
  can_decline_reopen: boolean
  /**
   * OKS-35 — Submission counts for the row's count chip + "View
   * submissions (N/3)" overflow item. All four keys always present;
   * `0` rather than missing.
   */
  submission_counts: SubmissionCounts
}

export interface BrandMissionCollabsPanel {
  rows: BrandMissionCollabRow[]
  total_count: number
  page_size: number
  /** True when ANY transition predicate may fire for the current user. */
  can_act: boolean
  /**
   * OKS-31 — Echoed `?state=<x>` URL param. `'all'` is the default; off-
   * whitelist values silently fall back to `'all'` server-side.
   */
  state_filter: MissionDashboardStateFilter
  /**
   * OKS-31 — Per-state counts including `'all'` aggregate. Sourced from
   * the same funnel aggregate that drives the bars, so the chip-row
   * label `Posted (6)` can never disagree with the funnel.
   */
  state_counts: Record<MissionDashboardStateFilter, number>
  /** OKS-31 — Pagy 5-key projection over the FILTERED row set. */
  pagination: MissionListPagination
}

// ─── Inertia error envelope (brief code-aware) ────────────────────────

/**
 * Brief errors carry a structured `code` (one of the 5 from
 * Missions::BriefSchemaValidator). Other fields ship as plain strings.
 * The form's render-prop `errors` map mixes the two — consumers read
 * brief.code via the helper below.
 */
export interface MissionBriefError {
  code:
    | "invalid_shape"
    | "invalid_top_node"
    | "disallowed_node"
    | "disallowed_mark"
    | "invalid_link_scheme"
  message: string
}

export type MissionFieldErrors = Record<
  string,
  string | string[] | MissionBriefError | MissionBriefError[] | undefined
>

/**
 * Pulls the first error message string from an arbitrarily-shaped Inertia
 * error entry (string / string[] / MissionBriefError / array thereof).
 * Returns null when no error is present.
 */
export function pickFirstErrorMessage(
  entry:
    | string
    | string[]
    | MissionBriefError
    | MissionBriefError[]
    | undefined,
): string | null {
  if (!entry) return null
  if (Array.isArray(entry)) {
    const first = entry.find((e) => Boolean(e))
    if (!first) return null
    return typeof first === "string" ? first : first.message
  }
  return typeof entry === "string" ? entry : entry.message
}

/**
 * Pulls EVERY error message from an Inertia error entry, in order.
 *
 * OKS-928 — `MissionsController#serialise_errors` uses
 * `errors.group_by_attribute`, so an attribute validated per-item (notably
 * `:files`, one error per rejected file) arrives as an ARRAY. Rendering it
 * through `pickFirstErrorMessage` would silently drop every message after
 * the first — the user is told one file was refused when three were. Use
 * this wherever an attribute can fail more than once; use
 * `pickFirstErrorMessage` for single-valued fields.
 *
 * Returns `[]` when no error is present, so callers can branch on length.
 */
export function collectErrorMessages(
  entry:
    | string
    | string[]
    | MissionBriefError
    | MissionBriefError[]
    | undefined,
): string[] {
  if (!entry) return []
  const list = Array.isArray(entry) ? entry : [entry]
  return list
    .filter((e) => Boolean(e))
    .map((e) => (typeof e === "string" ? e : e.message))
    .filter((m) => m.length > 0)
}

/** Pulls the first brief error code, if the entry carries structured codes. */
export function pickBriefErrorCode(
  entry: MissionFieldErrors["brief"],
): MissionBriefError["code"] | null {
  if (!entry) return null
  const first = Array.isArray(entry) ? entry[0] : entry
  if (!first) return null
  return typeof first === "string" ? null : (first.code ?? null)
}
